import React, { useState, useRef, useEffect } from 'react';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import {
  Modal,
  Box,
  Grid,
  Typography,
  IconButton,
  TableFooter,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import TableHead from '@mui/material/TableHead';
import Paper from '@mui/material/Paper';
import addCommas from '../../utils/functions/commas';
import { styled } from '@mui/material/styles';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import inetlogo from '../../assets/images/inetlogo.png';
import ImageListItem from '@mui/material/ImageListItem';
import { useLocation, useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';

const outerStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  // กำหนดความกว้างตามขนาดจอ
  width: { xs: '95%', sm: '80%', md: '60%', lg: '50%' },
  bgcolor: 'background.paper',
  p: 0,
  borderRadius: '15px',
  overflow: 'hidden',
  maxHeight: '90vh',
};

const innerStyle = {
  padding: { xs: 2, md: 4 }, // <--- 16px บนมือถือ, 32px บนจอใหญ่
  maxHeight: '90vh',
  overflowY: 'auto',
};

const setFont = {
  fontFamily: 'Monospace',
  fontSize: '12px',
  color: (theme) => theme.palette.lightGreen.main,
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

function DrawerTxnDetail({
  openDrawer,
  setOpenDrawer,
  dataRowDetail,
  setData,
}) {
  // console.log('dataRowDetail', dataRowDetail);
  const { t } = useTranslation();
  const pdfRef = useRef();
  const [isPdfCreating, setIsPdfCreating] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const methos = {
    closeButtonBeforeCreatePDF: async (msg) => {
      setIsPdfCreating(msg);
    },
  };
  const downloadPDF = async () => {
    await methos.closeButtonBeforeCreatePDF(true);
    await html2canvas(pdfRef.current).then(async (canvas) => {
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4', true);
      const pdfWidth = 210; // A4 ขนาด 210x297 มิลลิเมตร (แนะนำให้ใช้ค่าที่สอดคล้องกับขนาดกระดาษที่ต้องการ)
      const pdfHeight = 297; // Use getHeight() instead of getWidth() for height
      const margin = 10; // margin in millimeters

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      const ratio = Math.min(
        (pdfWidth - 2 * margin) / imgWidth,
        (pdfHeight - 2 * margin) / imgHeight,
      );
      const imgX = margin;
      const imgY = margin;

      pdf.addImage(
        imgData,
        'PNG',
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio,
      );
      pdf.setFontSize(12);
      pdf.setTextColor('#000000');
      pdf.save('invoice.pdf');
      await methos.closeButtonBeforeCreatePDF(false);
    });
  };

  const [result, setResult] = useState([]);

  const data = {
    detail: [
      {
        title: t('app.totalAmountThb'),
        data: 'total_amount',
      },
      {
        title: t('app.vatThb'),
        data: 'total_vat',
      },
      {
        title: t('app.whtThb'),
        data: 'total_wht',
      },
      {
        title: t('app.discountThb'),
        data: 'total_discount',
      },
      {
        title: t('app.lateFeeThb'),
        data: 'total_fines',
      },
      {
        title: t('app.grandTotalThb'),
        data: 'total_pay_amount',
      },
    ],

    table: [
      {
        HeadName: t('app.startDate'),
        data: 'period_start_date',
      },
      {
        HeadName: t('app.endDate'),
        data: 'period_end_date',
      },
      {
        HeadName: t('app.invoiceNo'),
        data: 'invoice_no',
      },
      {
        HeadName: t('app.payAmount'),
        data: 'pay_amount',
      },
      {
        HeadName: t('app.description'),

        data: 'materials.material_name',
      },
    ],
  };

  useEffect(() => {
    setResult(dataRowDetail?.result);
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return dayjs(dateString).locale('th').format('DD MMM YYYY');
  };

  const handleClose = () => {
    setOpenDrawer(false);
    setData({});
    navigate(location.pathname, { replace: true, state: {} });
  };

  return (
    <Modal
      open={openDrawer}
      onClose={(event, reason) => {
        if (reason !== 'backdropClick') {
          handleClose(event, reason);
        }
      }}
      aria-labelledby="transaction-detail-title"
      aria-describedby="transaction-detail-description"
    >
      <Box sx={outerStyle}>
        <Box sx={innerStyle}>
          <Grid container ref={pdfRef} spacing={1}>
            <Grid container spacing={1}>
              <Grid item xs={3}>
                <ImageListItem sx={{ maxWidth: '100px' }}>
                  <img src={`${inetlogo}`} />
                </ImageListItem>
              </Grid>
              <Grid
                item
                xs={5}
                sx={{
                  textAlign: 'left',
                  color: (theme) => theme.palette.grey[500],
                }}
              >
                <Stack
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  className="mt-7"
                >
                  <Typography sx={{ fontSize: '10px' }}>
                    บริษัท อินเตอร์เน็ตประเทศไทย จำกัด (มหาชน)
                  </Typography>
                  <Typography sx={{ fontSize: '10px' }}>
                    1768 อาคารไทยซัมมิท ทาวเวอร์ ชั้น 10-12 และชั้น IT
                  </Typography>
                  <Typography sx={{ fontSize: '10px' }}>
                    ถ.เพชรบุรีตัดใหม่ แขวงบางกระปิ เขตห้วยขวาง กรุงเทพ 10310
                  </Typography>
                </Stack>
              </Grid>
              <Grid item xs={4} sx={{ textAlign: 'right' }}>
                {!isPdfCreating && (
                  <div className="mt-4">
                    <IconButton
                      aria-label=""
                      size="small"
                      onClick={handleClose}
                      sx={{
                        border: `3px`,
                        borderRadius: '50%',
                        borderStyle: 'solid',
                        borderColor: (theme) => theme.palette.grey[900],
                      }}
                    >
                      <CloseIcon
                        fontSize="inherit"
                        sx={{
                          color: (theme) => theme.palette.grey[900],
                          strokeWidth: 1.5,
                          stroke: 'currentcolor',
                        }}
                      />
                    </IconButton>
                  </div>
                )}
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid
                sx={{ textAlign: 'center', marginBottom: '10px' }}
                item
                xs={12}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 'bold', color: '#545454' }}
                >
                  Transaction Detail
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Stack
                  direction="row"
                  justifyContent="flex-start"
                  alignItems="flex-start"
                  spacing={5}
                >
                  <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={0}
                  >
                    <Typography sx={{ fontSize: '12px' }}>
                      {t('app.customerCompanyName')}
                    </Typography>
                    <Typography sx={{ fontSize: '12px' }}>
                      {t('app.transactionRefNo')}
                    </Typography>

                    <Typography sx={{ fontSize: '12px' }}>
                      {t('app.to')}
                    </Typography>
                  </Stack>
                  <Stack
                    direction="column"
                    justifyContent="flex-start"
                    alignItems="flex-start"
                    spacing={0}
                  >
                    <Typography sx={setFont}>
                      {dataRowDetail?.customer?.customer_name}
                    </Typography>
                    <Typography sx={setFont}>
                      {dataRowDetail?.order_id_ref}
                    </Typography>

                    <Typography sx={setFont}>
                      {dataRowDetail?.sorgcode}
                    </Typography>
                  </Stack>
                </Stack>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <div className="mt-5">
                  <TableContainer
                    component={Paper}
                    sx={{
                      borderRadius: '10px',
                      border: '1px solid',
                      borderColor: (theme) => theme.palette.grey[400],
                      boxShadow: 'none',
                    }}
                  >
                    <Table sx={{}} size="small" aria-label="a dense table">
                      <TableHead>
                        <TableRow>
                          {data.table.map((item, i) => (
                            <StyledTableCell key={i} align="center">
                              {item.HeadName}
                            </StyledTableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {result?.map((row, index) => (
                          <TableRow key={index}>
                            {data.table.map((item, i) => {
                              const renderCellContent = () => {
                                if (item.data === 'materials.material_name') {
                                  return (
                                    row?.materials
                                      ?.map((mat) => mat.material_name)
                                      .join(', ') ?? '-'
                                  );
                                }
                                if (item.data === 'invoice_no') {
                                  return (
                                    // ใส่ style={{ fontFamily: 'ชื่อฟอนต์' }}
                                    <div style={{ fontFamily: 'Monospace' }}>
                                      {/* ค่าที่คุณต้องการแสดง */}
                                      {row?.[item.data] ?? '-'}
                                    </div>
                                  );
                                }

                                if (
                                  [
                                    'period_start_date',
                                    'period_end_date',
                                  ].includes(item.data)
                                ) {
                                  return formatDate(row?.[item.data] ?? '');
                                }

                                return row?.[item.data] ?? '-';
                              };

                              return (
                                <TableCell key={i} align="center">
                                  {renderCellContent()}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                      </TableBody>

                      {/* <TableFooter
                        sx={{
                          backgroundColor: (theme) =>
                            theme.palette.primary.main,
                        }}
                      >
                        <TableCell
                          sx={{ color: (theme) => theme.palette.common.white }}
                          align="center"
                        >
                          Total
                        </TableCell>
                        <TableCell
                          sx={{ color: (theme) => theme.palette.common.white }}
                          align="center"
                        >
                          Total
                        </TableCell>
                        <TableCell
                          sx={{ color: (theme) => theme.palette.common.white }}
                          align="center"
                        >
                          Total
                        </TableCell>
                      </TableFooter> */}
                    </Table>
                  </TableContainer>
                </div>
              </Grid>
            </Grid>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <div className="mt-5">
                  <Typography
                    variant="h6"
                    sx={{
                      color: (theme) => theme.palette.success.dark,
                      fontWeight: 'bold',
                    }}
                  >
                    {t('app.detail')}
                  </Typography>
                  <TableContainer
                    component={Paper}
                    sx={{
                      border: '0.1px solid',
                      borderColor: (theme) => theme.palette.grey[400],
                      boxShadow: 'none',
                      marginTop: 3,
                    }}
                  >
                    <Table sx={{}} size="small" aria-label="a dense table">
                      <TableBody>
                        {data.detail.map((item, index) => (
                          <TableRow key={index}>
                            <TableCell align="left">{item.title}</TableCell>
                            <TableCell align="right">
                              {addCommas(dataRowDetail?.[`${item.data}`])}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </div>
              </Grid>
            </Grid>
          </Grid>
          <Grid container spacing={1} sx={{ marginTop: '10px' }}>
            <Grid item sx={{ textAlign: 'center' }} xs={12}>
              <Button
                variant="contained"
                sx={{ color: 'white' }}
                onClick={downloadPDF}
              >
                <div className="mr-3">
                  <CloudDownloadIcon />
                </div>
                {t('app.downloadFile')}
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>
    </Modal>
  );
}

export default DrawerTxnDetail;
